/**
 * /reset-password?token=<...> — Story 1c (apply).
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthCard } from '../../components/auth/auth-card.tsx';
import { Button, Input } from '@/components/ui';
import { authApi } from '../../state/auth/api.ts';

const schema = z.object({
  newPassword: z.string().min(10, 'password must be at least 10 characters'),
  confirmPassword: z.string(),
}).refine((v) => v.newPassword === v.confirmPassword, {
  message: 'passwords do not match',
  path: ['confirmPassword'],
});
type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [serverErrorCode, setServerErrorCode] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    setServerErrorCode(null);
    const res = await authApi.resetPassword({
      token,
      newPassword: values.newPassword,
    });
    if (!res.ok) {
      setServerErrorCode(res.error.code);
      setServerError(
        res.error.code === 'invalid_token'
          ? 'This reset link has expired or already been used. Request a new one.'
          : res.error.message || 'Something went wrong. Please try again.',
      );
      return;
    }
    navigate('/post-sign-in');
  });

  if (!token) {
    return (
      <AuthCard
        eyebrow="Reset password"
        title="Missing reset token."
        lede="Open the reset link from your email — it includes the token this page needs."
        footer={
          <Link className="text-ink underline hover:text-accent" to="/forgot-password">
            Request a new reset link
          </Link>
        }
      >
        <p className="font-sans text-body text-muted">
          The URL doesn&apos;t include a reset token. If you opened this page directly,
          go back to your email and click the link.
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      eyebrow="Reset password"
      title="Set a new password."
      footer={
        <Link className="text-ink underline hover:text-accent" to="/sign-in">
          Back to sign in
        </Link>
      }
    >
      <form className="flex flex-col gap-5" onSubmit={onSubmit} noValidate>
        <Input
          label="New password"
          id="rp-new"
          helperText="At least 10 characters."
          error={errors.newPassword?.message}
          type="password"
          autoComplete="new-password"
          {...register('newPassword')}
        />
        <Input
          label="Confirm new password"
          id="rp-confirm"
          error={errors.confirmPassword?.message}
          type="password"
          autoComplete="new-password"
          {...register('confirmPassword')}
        />
        {serverError ? (
          <div className="flex flex-col gap-2">
            <p className="font-sans text-small text-error" role="alert">
              {serverError}
            </p>
            {serverErrorCode === 'invalid_token' ? (
              <Link className="text-ink underline hover:text-accent" to="/forgot-password">
                Request a new reset link
              </Link>
            ) : null}
          </div>
        ) : null}
        <Button variant="primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Resetting…' : 'Set new password'}
        </Button>
      </form>
    </AuthCard>
  );
}
